<template>
    <b-modal
        size="lg"
        v-model="modalShow"
        title="質問スケジュール編集"
        @ok="handleOk"
        @cancel="close"
        @hidden="close">
        <form
            v-if="group !== null && detail !== null"
            @submit.stop.prevent="onSubmit">
            <div class="row">
                <div class="col-12">
                    <div class="form-group m-form__group">
                        <div class="d-flex align-items-center">
                            <label
                                class="inlineLabel"
                                for="health_survey_template_id">
                                質問デッキ
                            </label>
                            <select
                                v-model="detail.health_survey_template_id"
                                :disabled="inProgress || hasSurvey"
                                class="form-control inlineInput"
                                :class="{ 'is-invalid' : errors.health_survey_template_id }">
                                <option
                                    key="health_survey_template_id"
                                    :value="null">
                                    質問デッキを選択
                                </option>
                                <option
                                    v-for="(template, index) in healthSurveyTemplates"
                                    :key="`health_survey_template_id_${index}`"
                                    :value="template.id">
                                    {{ template.name }}
                                </option>
                            </select>
                            <div
                                v-if="healthSurveyTemplate !== null"
                                class="text-muted ml-1">
                                {{ `（設問数：${healthSurveyTemplate.questions_count}問）` }}
                            </div>
                        </div>
                        <div
                            v-if="errors.health_survey_template_id"
                            class="invalid-feedback d-block mt-1">
                            {{ errors.health_survey_template_id[0] }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-12">
                    <div class="form-group m-form__group">
                        <div class="d-flex align-items-center">
                            <label
                                class="inlineLabel"
                                for="period_start_date">
                                開始日
                            </label>
                            <input
                                v-model="detail.period_start_date"
                                :disabled="inProgress || hasSurvey"
                                type="date"
                                id="period_start_date"
                                class="form-control inlineInput"
                                :class="{ 'is-invalid' : errors.period_start_date }">
                            <b-button
                                type="button"
                                class="btn ml-3"
                                variant="yokumiru-gray"
                                :disabled="inProgress || hasSurvey"
                                @click="onClickPeriodStartDateTomorrow">
                                明日
                            </b-button>
                            <b-button
                                type="button"
                                class="btn ml-2"
                                variant="yokumiru-gray"
                                :disabled="inProgress || hasSurvey"
                                @click="onClickPeriodStartDateEndOfMonth">
                                今月末
                            </b-button>
                        </div>
                        <div
                            v-if="errors.period_start_date"
                            class="invalid-feedback d-block mt-1">
                            {{ errors.period_start_date[0] }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-12">
                    <div class="form-group m-form__group">
                        <div class="d-flex align-items-center">
                            <label
                                class="inlineLabel"
                                for="period">
                                周期
                            </label>
                            <select
                                v-model="detail.period"
                                @change="onChangePeriod"
                                :disabled="inProgress || hasSurvey"
                                class="form-control inlineInput"
                                :class="{ 'is-invalid' : errors.period }">
                                <option
                                    v-for="(period, index) in periods"
                                    :key="`period_${index}`"
                                    :value="period.value">
                                    {{ period.text }}
                                </option>
                            </select>
                            <template v-if="detail.period === 'MONTHLY'">
                                <div class="ml-2 mr-2">の</div>
                                <select
                                    v-model="detail.period_day"
                                    :disabled="inProgress || hasSurvey"
                                    class="form-control inlineInput"
                                    :class="{ 'is-invalid' : errors.period_day }">
                                    <option
                                        v-for="(day, index) in days"
                                        :key="`period_day_${index}`"
                                        :value="day">
                                        {{ `${day}日` }}
                                    </option>
                                </select>
                            </template>
                            <template v-if="detail.period === 'WEEKLY'">
                                <div class="ml-2 mr-2">の</div>
                                <select
                                    v-model="detail.period_day_of_week"
                                    :disabled="inProgress || hasSurvey"
                                    class="form-control inlineInput"
                                    :class="{ 'is-invalid' : errors.period_day_of_week }">
                                    <option
                                        v-for="(dayOfWeek, index) in dayOfWeeks"
                                        :key="`period_day_of_week_${index}`"
                                        :value="dayOfWeek.value">
                                        {{ `${dayOfWeek.text}曜日` }}
                                    </option>
                                </select>
                            </template>
                        </div>
                        <div
                            v-if="errors.period"
                            class="invalid-feedback d-block mt-1">
                            {{ errors.period[0] }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-12">
                    <div class="form-group m-form__group">
                        <div class="d-flex align-items-center">
                            <label
                                class="inlineLabel"
                                for="period_end_date">
                                終了日
                            </label>
                            <input
                                v-model="detail.period_end_date"
                                :disabled="inProgress || hasSurvey"
                                type="date"
                                id="period_end_date"
                                class="form-control inlineInput"
                                :class="{ 'is-invalid' : errors.period_end_date }">
                            <b-button
                                type="button"
                                class="btn ml-3"
                                variant="yokumiru-gray"
                                :disabled="inProgress || hasSurvey"
                                @click="onClickPeriodEndDateEndOfNextMonth">
                                来月末
                            </b-button>
                            <b-button
                                type="button"
                                class="btn ml-2"
                                variant="yokumiru-gray"
                                :disabled="inProgress || hasSurvey"
                                @click="onClickPeriodEndDateHalfYearLater">
                                半年後
                            </b-button>
                            <b-button
                                type="button"
                                class="btn ml-2"
                                variant="yokumiru-gray"
                                :disabled="inProgress || hasSurvey"
                                @click="onClickPeriodEndDate1YearLater">
                                1年後
                            </b-button>
                        </div>
                        <div
                            v-if="errors.period_end_date"
                            class="invalid-feedback d-block mt-1">
                            {{ errors.period_end_date[0] }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-12">
                    <div class="form-group m-form__group">
                        <div class="d-flex align-items-center">
                            <label
                                class="inlineLabel"
                                for="is_enabled">
                                ステータス
                            </label>
                            <select
                                v-model="detail.is_enabled"
                                :disabled="inProgress"
                                class="form-control inlineInput"
                                :class="{ 'is-invalid' : errors.is_enabled }">
                                <option
                                    key="is_enabled_true"
                                    :value="true">
                                    利用する
                                </option>
                                <option
                                    key="is_enabled_false"
                                    :value="false">
                                    利用しない
                                </option>
                            </select>
                        </div>
                        <div
                            v-if="errors.is_enabled"
                            class="invalid-feedback d-block mt-1">
                            {{ errors.is_enabled[0] }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-12">
                    <div class="form-group m-form__group">
                        <div class="d-flex">
                            <label class="inlineLabel">
                                直近作成予定日
                            </label>
                            <div v-if="limitedEstimateList.length > 0">
                                <ul class="estimateList" :class="{ 'estimateListMuted' : !detail.is_enabled }">
                                    <li v-for="estimate in limitedEstimateList">
                                        {{ estimateFormat(estimate) }}
                                    </li>
                                </ul>
                            </div>
                            <div v-else class="text-muted">&nbsp;-</div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-12">
                    <div class="form-group m-form__group">
                        <div class="d-flex align-items-center">
                            <label
                                class="inlineLabel"
                                for="due_within_days">
                                回答期限
                            </label>
                            <select
                                v-model="detail.due_within_days"
                                :disabled="inProgress || hasSurvey"
                                class="form-control inlineInput"
                                :class="{ 'is-invalid' : errors.due_within_days }">
                                <option
                                    v-for="(day, index) in days"
                                    :key="`due_within_days_${index}`"
                                    :value="day">
                                    {{ `${day}日` }}
                                </option>
                            </select>
                            <div class="ml-2">以内</div>
                        </div>
                        <div
                            v-if="errors.period"
                            class="invalid-feedback d-block mt-1">
                            {{ errors.period[0] }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-12">
                    <div class="form-group m-form__group">
                        <div class="d-flex align-items-center">
                            <label
                                class="inlineLabel"
                                for="max_daily_question">
                                1日の質問数
                            </label>
                            <select
                                v-model="detail.max_daily_question"
                                :disabled="inProgress || hasSurvey"
                                class="form-control inlineInput"
                                :class="{ 'is-invalid' : errors.max_daily_question }">
                                <option
                                    key="max_daily_question"
                                    :value="null">
                                    選択してください
                                </option>
                                <option
                                    v-for="(num, index) in maxDailyQuestionNums"
                                    :key="`max_daily_question_${index}`"
                                    :value="num">
                                    {{ `${num}問` }}
                                </option>
                            </select>
                        </div>
                        <div
                            v-if="errors.max_daily_question"
                            class="invalid-feedback d-block mt-1">
                            {{ errors.max_daily_question[0] }}
                        </div>
                    </div>
                </div>
            </div>
        </form>

        <div slot="modal-footer">
            <button
                class="btn btn-yokumiru-orange float-right"
                :disabled="inProgress"
                @click="handleOk">
                保存
            </button>
            <button
                class="btn btn-yokumiru-gray float-right mr-2"
                :disabled="inProgress"
                @click="close">
                キャンセル
            </button>
        </div>
    </b-modal>
</template>

<script>
import moment from 'moment-timezone'
import constants from '../../../constants'
import YokumiruHelper from '../../../YokumiruHelper'

export default {
    data: function () {
        return {
            helper: YokumiruHelper,
            inProgress: false,
            modalShow: false,
            healthSurveyTemplates: [],
            estimateList: [],
            group: null,
            detail: null,
            errors: {},
        }
    },

    computed: {
        /**
         * 回答データが存在するかどうか
         * @return {boolean}
         */
        hasSurvey: function () {
            return this.detail && this.detail.health_surveys_count > 0
        },

        /**
         * 直近作成予定日を3件まで表示
         * @return array
         */
        limitedEstimateList: function () {
            let count = 0
            return this.estimateList.map((estimate) => {
                count++
                return (count <= 3) ? estimate : null
            }).filter((v) => v)
        },

        /**
         * 直近作成予定日の見積もりのためのスケジュール設定項目を埋めているかどうか
         * @return {boolean}
         */
        isFilledForEstimate: function () {
            if (!this.detail) return false
            if (!this.detail.health_survey_template_id) return false
            // if (!this.detail.max_daily_question) return false
            if (!this.detail.period_start_date) return false
            if (!this.detail.period_end_date) return false
            if (!this.detail.period) return false
            if (!this.detail.due_within_days) return false

            if (this.detail.period === 'MONTHLY') {
                if (!this.detail.period_day) return false
            }
            if (this.detail.period === 'WEEKLY') {
                if (!this.detail.period_day_of_week) return false
            }
            return true
        },

        periods: function () {
            return [
                { value: 'MONTHLY', text: '毎月' },
                { value: 'WEEKLY', text: '毎週' },
                { value: 'DAILY', text: '毎日' },
            ]
        },

        days: function () {
            return [...Array(31).keys()].map(i => i + 1)
        },

        dayOfWeeks: function () {
            return [
                { value: 1, text: '月' },
                { value: 2, text: '火' },
                { value: 3, text: '水' },
                { value: 4, text: '木' },
                { value: 5, text: '金' },
                { value: 6, text: '土' },
                { value: 0, text: '日' },
            ]
        },

        healthSurveyTemplate: function () {
            if (!this.detail.health_survey_template_id) {
                return null
            }
            return this.healthSurveyTemplates.find((t) => {
                return t.id === this.detail.health_survey_template_id
            })
        },

        maxDailyQuestionNums: function () {
            if (this.healthSurveyTemplate) {
                const count = this.healthSurveyTemplate.questions_count
                return [...Array(count).keys()].map(i => i + 1)
            }
            return []
        },
    },

    watch: {
        'detail.health_survey_template_id': function () {
            this.fetchHealthSurveyEstimate()
        },
        'detail.period_start_date': function () {
            this.fetchHealthSurveyEstimate()
        },
        'detail.period_end_date': function () {
            this.fetchHealthSurveyEstimate()
        },
        'detail.due_within_days': function () {
            this.fetchHealthSurveyEstimate()
        },
        'detail.period_day': function () {
            this.fetchHealthSurveyEstimate()
        },
        'detail.period_day_of_week': function () {
            this.fetchHealthSurveyEstimate()
        },
    },

    created: function () {
        this.fetchHealthSurveyTemplates()
        bus.$on('open-modal-admin-edit-health-survey-group-detail', this.open)
    },

    beforeDestroy: function () {
        bus.$off('open-modal-admin-edit-health-survey-group-detail')
    },

    methods: {
        /**
         * モーダル表示
         * @param {object} group
         * @param {object} healthSurveyGroupDetail
         */
        open: function (group, healthSurveyGroupDetail) {
            this.group = {...group}
            this.detail = {...healthSurveyGroupDetail}
            this.modalShow = true
        },

        /**
         * モーダルを閉じる
         */
        close: function () {
            this.group = null
            this.detail = null
            this.errors = {}
            this.modalShow = false
        },

        /**
         * メンタルチェック質問デッキ一覧取得
         */
        fetchHealthSurveyTemplates: function () {
            axios.get(constants.admin.health_survey_templates.endpoint.base)
                .then(({data}) => {
                    this.healthSurveyTemplates = data.data
                })
        },

        /**
         * 直近作成予定日の一覧取得
         */
        fetchHealthSurveyEstimate: async function () {
            if (!this.isFilledForEstimate) return
            if (this.inProgress) return
            this.inProgress = true

            const endpoint = constants.admin.health_survey_group_details.endpoint.base

            await axios.get(`${endpoint}/estimate${this.buildQueryForEstimate()}`)
                .then(({data}) => {
                    this.estimateList = data.data
                })

            this.inProgress = false
        },

        buildQueryForEstimate: function () {
            let query = '?'
            query += `health_survey_template_id=${this.detail.health_survey_template_id || ''}`
            // query += `&max_daily_question=${this.detail.max_daily_question || ''}`
            query += `&period_start_date=${this.detail.period_start_date || ''}`
            query += `&period_end_date=${this.detail.period_end_date || ''}`
            query += `&period=${this.detail.period || ''}`
            query += `&period_day=${this.detail.period_day || ''}`
            query += `&period_day_of_week=${this.detail.period_day_of_week || ''}`
            query += `&due_within_days=${this.detail.due_within_days || ''}`
            return query
        },

        /**
         * 直近作成予定日フォーマット
         * @param {object} estimate
         * @return {string}
         */
        estimateFormat: function (estimate) {
            return moment(`${estimate.start_date} 00:00:00`).format('YYYY年M月D日 (ddd)')
        },

        /**
         * ボタン押下（開始日を明日に変更）
         */
        onClickPeriodStartDateTomorrow: function () {
            this.detail.period_start_date = moment().add(1, 'days').format('YYYY-MM-DD')
        },

        /**
         * ボタン押下（開始日を今月末に変更）
         */
        onClickPeriodStartDateEndOfMonth: function () {
            this.detail.period_start_date = moment().endOf('month').format('YYYY-MM-DD')
        },

        /**
         * ボタン押下（終了日を来月末に変更）
         */
        onClickPeriodEndDateEndOfNextMonth: function () {
            this.detail.period_end_date = moment().endOf('month').add(1, 'months').format('YYYY-MM-DD')
        },

        /**
         * ボタン押下（終了日を半年後に変更）
         */
        onClickPeriodEndDateHalfYearLater: function () {
            this.detail.period_end_date = moment().add(6, 'months').format('YYYY-MM-DD')
        },

        /**
         * ボタン押下（終了日を半年後に変更）
         */
        onClickPeriodEndDate1YearLater: function () {
            this.detail.period_end_date = moment().add(1, 'years').format('YYYY-MM-DD')
        },

        /**
         * 周期変更時
         */
        onChangePeriod: function () {
            this.detail.period_day = 1
            this.detail.period_day_of_week = 1
            this.fetchHealthSurveyEstimate()
        },

        /**
         * 更新ボタン押下時
         * @param {object} e
         */
        handleOk: function (e) {
            e.preventDefault()
            this.onSubmit()
        },

        /**
         * 質問スケジュール追加
         */
        onSubmit: async function () {
            if (this.inProgress) return
            this.inProgress = true

            const endpoint = constants.admin.groups.endpoint.base
            const params = {...this.detail}

            if (this.detail.period === 'MONTHLY') {
                params.period_day_of_week = null
            }
            else if (this.detail.period === 'WEEKLY') {
                params.period_day = null
            }
            else if (this.detail.period === 'DAILY') {
                params.period_day_of_week = null
                params.period_day = null
            }

            await axios.put(`${endpoint}/${this.group.id}/health_survey_group_details/${this.detail.id}`, params)
                .then(() => {
                    bus.$emit('alert-success', 'スケジュールを追加しました。')
                    bus.$emit('health-survey-group-details-edited')
                    this.errors = {}
                    this.close()
                })
                .catch(({response}) => {
                    if (response.hasOwnProperty('data') && response.data.hasOwnProperty('errors')) {
                        bus.$emit('alert-danger')
                        this.errors = response.data.errors
                    }
                    else {
                        bus.$emit('alert-danger', 'システムエラーが発生しました。')
                    }
                })

            this.inProgress = false
        },
    },
}
</script>

<style scoped lang="scss">
.inlineLabel {
    width: 140px;
    margin-bottom: 0;
    padding-bottom: 0;
}

.inlineInput {
    width: 180px;
}

.estimateList {
    list-style-type: none;
    margin-left: 0;
    padding-left: 0;

    &.estimateListMuted {
        color: #A2A8AD
    }
}
</style>
